import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { useState } from 'react';

import { listRecoveryEvents, setRecoveryOverride } from '@/api/findings';
import type { FindingOut } from '@/api/types';
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { coerceCurrency, formatCurrency, formatDateTime } from '@/lib/format';
import { describeRecoveryEdit } from '@/lib/recoveryHistory';
import { taxIncludedRecovery, TAX_RATE_SHORTCUTS } from '@/lib/recoveryShortcuts';

/** The recoverable amount on the finding detail page, editable in place.
 *  Any user who can reach this page can override the engine's number — the
 *  page itself is behind the practitioner RoleGuard, so no second
 *  per-control role check is needed. The edit records who/when (shown by
 *  `RecoveryHistoryPanel` below).
 *
 *  `billAmount` (the tax-included bill total from the finding's evidence)
 *  powers the tax-rate quick-fill shortcuts under the input. */
export function RecoveryAmountEditor({
  finding,
  amountClassName,
  billAmount,
}: {
  finding: FindingOut;
  amountClassName: string;
  billAmount: string | null;
}) {
  const { getIdToken } = useAuth();
  const qc = useQueryClient();
  const currency = coerceCurrency(finding.recovery.currency);
  const isOverridden = finding.recovery_amount_original !== null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  // Quick-fill options that back the embedded tax out of the tax-included
  // bill total (bill × rate/(100+rate)). All-or-nothing: if the bill amount
  // isn't a positive number, every rate yields null and the row is hidden.
  const shortcuts = TAX_RATE_SHORTCUTS.map((rate: number) => ({
    rate,
    value: taxIncludedRecovery(billAmount, rate),
  })).filter((s): s is { rate: number; value: string } => s.value !== null);

  const save = useMutation({
    mutationFn: async (next: string | null) =>
      setRecoveryOverride(finding.finding_id, next, (await getIdToken()) ?? ''),
    onSuccess: () => {
      // Prefix-match invalidation refreshes this finding (the detail page's
      // single-finding query), the run's finding list (Review queue /
      // Suppliers), and the recovery-edit history so the new value +
      // attribution appear everywhere it's shown.
      void qc.invalidateQueries({ queryKey: ['finding', finding.finding_id] });
      void qc.invalidateQueries({ queryKey: ['findings', 'by-run', finding.audit_run_id] });
      void qc.invalidateQueries({ queryKey: ['runs', finding.audit_run_id] });
      void qc.invalidateQueries({ queryKey: ['recovery-events', finding.finding_id] });
      setEditing(false);
    },
  });

  const startEdit = () => {
    setDraft(finding.recovery.amount);
    save.reset();
    setEditing(true);
  };

  if (editing) {
    return (
      <span className="inline-flex flex-col gap-1.5">
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <input
            type="text"
            inputMode="decimal"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draft.trim() && !save.isPending) save.mutate(draft.trim());
              if (e.key === 'Escape') setEditing(false);
            }}
            className="w-32 rounded border border-border bg-background px-2 py-0.5 text-lg font-semibold text-brand-navy"
            aria-label="Recoverable amount"
          />
          <Button
            type="button"
            size="sm"
            className="h-7 text-xs"
            disabled={save.isPending || !draft.trim()}
            onClick={() => save.mutate(draft.trim())}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            disabled={save.isPending}
            onClick={() => setEditing(false)}
          >
            Cancel
          </Button>
          {isOverridden && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-brand-slate"
              disabled={save.isPending}
              onClick={() => save.mutate(null)}
              title="Discard the override and revert to the engine's number"
            >
              Reset to engine
            </Button>
          )}
          {save.isError && <span className="text-xs text-destructive">Couldn&rsquo;t save.</span>}
        </span>
        {shortcuts.length > 0 && billAmount !== null && (
          <span className="inline-flex flex-wrap items-center gap-1 text-xs font-normal text-brand-slate">
            <span>Tax in {formatCurrency(billAmount, currency)}:</span>
            {shortcuts.map(({ rate, value }) => (
              <Button
                key={rate}
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-xs"
                disabled={save.isPending}
                onClick={() => setDraft(value)}
                title={`${rate}% tax embedded in the tax-included total — ${formatCurrency(
                  billAmount,
                  currency,
                )} × ${rate}/${100 + rate} = ${formatCurrency(value, currency)}`}
              >
                {rate}%
              </Button>
            ))}
          </span>
        )}
      </span>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-baseline gap-1.5">
      <span className={amountClassName}>{formatCurrency(finding.recovery.amount, currency)}</span>
      {isOverridden && (
        <span className="text-xs font-normal text-amber-600" title="Overridden by a practitioner">
          edited · was {formatCurrency(finding.recovery_amount_original ?? '0', currency)}
        </span>
      )}
      <button
        type="button"
        onClick={startEdit}
        aria-label="Edit recoverable amount"
        className="text-brand-slate hover:text-brand-royal"
      >
        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </span>
  );
}

/** Attribution + full edit history for the recoverable amount. Shows
 *  "Engine-calculated" until a practitioner edits it, then "Edited by
 *  brian · <date>" plus a collapsible trail of every edit. */
export function RecoveryHistoryPanel({ finding }: { finding: FindingOut }) {
  const { userKey, getIdToken } = useAuth();
  const query = useQuery({
    queryKey: ['recovery-events', finding.finding_id, userKey],
    queryFn: async () => listRecoveryEvents(finding.finding_id, (await getIdToken()) ?? ''),
  });
  const items = query.data?.items ?? [];

  // `recovery_amount_original !== null` is the authoritative "an override is
  // active" signal — `recovery_override_by` is nullable (an actor without a
  // derivable alias), so keying attribution off it would mislabel a real
  // override as engine-calculated.
  const attribution =
    finding.recovery_amount_original !== null
      ? `Recoverable amount edited by ${finding.recovery_override_by ?? 'an unknown practitioner'}${
          finding.recovery_override_at ? ` · ${formatDateTime(finding.recovery_override_at)}` : ''
        }`
      : 'Recoverable amount calculated by the engine';

  return (
    <div className="grid gap-1 text-xs text-brand-slate">
      <span>{attribution}</span>
      {items.length > 0 && (
        <details>
          <summary className="cursor-pointer select-none text-brand-royal underline-offset-4 hover:underline">
            Edit history ({items.length})
          </summary>
          <ul className="mt-1.5 grid gap-1 border-l border-border pl-3">
            {items.map((e, i) => (
              <li key={i}>
                {describeRecoveryEdit(e, finding.recovery.currency)}
                {' · '}
                {formatDateTime(e.changed_at)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
